import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { DeleteRequestData, GetAllDeleteRequestsAsync } from "../../Admin-Api/GetAllDeleteRequestsAsync";
import { MoveAddressToAddressDeletedTableAsync } from "../../Admin-Api/MoveAddressToAddressDeletedTableAsync"; 

interface CustomJwtPayload {
  RoleId: string;
}

const DeleteRequestSkeleton = () => (
  <div className="animate-pulse">
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
        <div className="flex items-end">
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>

      </div>
    </div>

    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm ">
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 grid grid-cols-9 gap-6 border-b">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="h-5 bg-gray-200 rounded"></div>
        ))}
      </div>

      <div className="divide-y divide-gray-100">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="p-4 grid grid-cols-9 gap-6">
            {[...Array(9)].map((_, j) => (
              <div key={j} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ViewDeleteRequest: React.FC = () => {
  const navigate = useNavigate();

  const [allDeleteRequests, setAllDeleteRequests] = useState<DeleteRequestData[]>([]);
  const [displayedRequests, setDisplayedRequests] = useState<DeleteRequestData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [selectedRequests, setSelectedRequests] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteRequestData | null>(null);

  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      navigate("/");
    } else {
      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(token);
        if (decodedToken.RoleId !== "1") {
          toast.error("Unauthorized access: RoleId is not 1");
          navigate("/");
        }
      } catch (error) {
        // console.error("Error decoding token:", error);
        toast.error("Invalid token. Please log in again.");
        navigate("/");
      }
    }
  }, [navigate]);

  const fetchDeleteRequests = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No auth token found");
      }
      const data = await GetAllDeleteRequestsAsync(token);
      setAllDeleteRequests(data);
      setDisplayedRequests(data);
      setSelectedRequests(new Set());
      setSelectAll(false);
    } catch (error) {
      // console.error("Error fetching delete requests:", error);
      toast.error("Error fetching delete requests.");
    } finally {
      setIsLoading(false);
    }
  };

  const filterDeleteRequestsByDate = () => {
  if (!startDate || !endDate) {
    if (!toast.isActive("select-dates")) {
      toast.info("Please select a start date and end date.", {
        toastId: "select-dates",
      });
    }
    return;
  }

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const filtered = allDeleteRequests.filter((request) => {
    const requestDate = new Date(request.createdDate);
    return requestDate >= start && requestDate <= end;
  });

  if (filtered.length === 0) {
    if (!toast.isActive("no-requests")) {
      toast.info(
        "No delete requests were submitted within the selected date range.",
        { toastId: "no-requests" }
      );
    }
  }

  setDisplayedRequests(filtered);
  setSelectedRequests(new Set());
  setSelectAll(false);
};

  useEffect(() => {
    fetchDeleteRequests();
  }, []);

  const handleRowClick = (index: number) => {
    const requestData = displayedRequests[index];
    navigate(`/delete-request-details/${index}`, { state: { deleteRequestData: requestData } });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, requestId: number) => {
    e.stopPropagation();
    const updated = new Set(selectedRequests);
    if (e.target.checked) {
      updated.add(requestId);
    } else {
      updated.delete(requestId);
    }
    setSelectedRequests(updated);
    if (updated.size !== displayedRequests.length) {
      setSelectAll(false);
    } else {
      setSelectAll(true);
    }
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allIds = displayedRequests.map((req) => req.id);
      setSelectedRequests(new Set(allIds));
    } else {
      setSelectedRequests(new Set());
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    if (endDate && endDate < newStartDate) {
      setEndDate("");
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handleDeleteClick = (e: React.MouseEvent, request: DeleteRequestData) => {
    e.stopPropagation();
    setDeleteTarget(request);
    setShowDeleteModal(true);
  };

  const handleBulkDeleteClick = () => {
    if (selectedRequests.size === 0) {
      if (!toast.isActive("select-requests")) {
      toast.info("Please select at least one request to delete", { toastId: "select-requests" });
      }
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No auth token found");
      }

      const deletePromises = Array.from(selectedRequests).map(async (requestId) => {
        const request = displayedRequests.find(req => req.id === requestId);
        if (request) {
          return MoveAddressToAddressDeletedTableAsync(request.addressId, token);
        }
      });

      const results = await Promise.all(deletePromises);
      
      const newAll = allDeleteRequests.filter(req => !selectedRequests.has(req.id));
      const newDisplayed = displayedRequests.filter(req => !selectedRequests.has(req.id));
      
      setAllDeleteRequests(newAll);
      setDisplayedRequests(newDisplayed);
      setSelectedRequests(new Set());
      setSelectAll(false);
      
      // toast.success("Selected requests have been deleted successfully");
    } catch (error) {
      // console.error("Error during bulk deletion:", error);
      toast.error("Error deleting the selected records.");
    } finally {
      setShowBulkDeleteModal(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await MoveAddressToAddressDeletedTableAsync(deleteTarget.addressId, token);
      if (response.status) {
        // toast.success(response.message);
        const newAll = allDeleteRequests.filter((req) => req.id !== deleteTarget.id);
        const newDisplayed = displayedRequests.filter((req) => req.id !== deleteTarget.id);
        setAllDeleteRequests(newAll);
        setDisplayedRequests(newDisplayed);
        const updatedSelected = new Set(selectedRequests);
        updatedSelected.delete(deleteTarget.id);
        setSelectedRequests(updatedSelected);
      } else {
        toast.error("Deletion failed.");
      }
    } catch (error) {
      // console.error("Error during deletion:", error);
      toast.error("Error deleting the record.");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="h-screen overflow-auto bg-magnolia select-none">
      <div className="flex flex-col min-h-screen">
        <header className="flex items-center justify-between px-8 py-4 bg-white/90 backdrop-blur-sm shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
              Manage Delete Requests
            </h1>
          </div>
        </header>

        <main className="flex-1 overflow-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <DeleteRequestSkeleton />
            ) : (
              <>
                <div className="flex flex-col sm:flex-row items-center justify-between mb-8">
                  <h4 className="text-xl text-gray-800 font-semibold flex items-center gap-3">
                    Delete Request Management
                    {displayedRequests.length > 0 && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                        {displayedRequests.length} Records
                      </span>
                    )}
                  </h4>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Start Date
                      </label>

                      <input
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 
                        focus:outline-none focus:border-blue-500 
                        transition-all"
                      />
                    </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      End Date
                    </label>

                    <input
                      type="date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      min={startDate || ""}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 
                      focus:outline-none focus:border-blue-500 
                      transition-all disabled:bg-gray-100"
                    />
                  </div>
                    <div className="flex items-end">
                      <button
                        onClick={filterDeleteRequestsByDate}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-end ">
                      <button
                        onClick={handleBulkDeleteClick}
                        className="px-4 py-2 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white flex items-center gap-2"
                      >
                        <span>Delete Selected</span>
                        {selectedRequests.size > 0 && (
                          <span className="bg-red-700 text-white px-2 py-0.5 rounded-full text-sm">
                            {selectedRequests.size}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {displayedRequests.length > 0 && (
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-x-auto">
                    <table className="min-w-full table-fixed">
                      <thead>
                        <tr className="bg-gradient-to-r  bg-blue-500 border-b">
                          <th className="px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAllChange}
                            />
                          </th>
                          {[
                            "User Name",
                            "Email",
                            "Phone Number",
                            "Delete Reason",
                            "Address",
                          ].map((header) => (
                            <th
                              key={header}
                              className="px-4 py-2 text-center text-sm font-semibold text-black"
                            >
                              {header}
                            </th>
                          ))}
                          <th className="px-4 py-2 text-center text-sm font-semibold text-black">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      {/* Table Body */}
                      <tbody className="divide-y divide-gray-100">
                        {displayedRequests.map((request, index) => (
                          <tr
                            key={request.id}
                            className="hover:bg-blue-50/50 cursor-pointer transition-colors duration-150"
                            onClick={() => handleRowClick(index)}
                          >
                            <td className="px-4 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={selectedRequests.has(request.id)}
                                onChange={(e) => handleCheckboxChange(e, request.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-4 py-2 text-center text-sm font-semibold text-black">
                              <span className="text-sm break-words hover:line-clamp-none">
                                {request.userName}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center text-sm font-semibold text-black">
                              <span className="text-sm break-words hover:line-clamp-none">
                                {request.email}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center text-sm font-semibold text-black">
                              <span className="text-sm line-clamp-3 hover:line-clamp-none">
                                {request.phoneNumber}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center text-sm font-semibold text-black">
                              <div className="text-sm whitespace-normal break-all overflow-hidden line-clamp-3 hover:line-clamp-none transition-all duration-300">
                                {request.deleteReason}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-center text-sm font-semibold text-black">
                              <span className="text-sm line-clamp-3 hover:line-clamp-none">
                                {request.addressLine1}
                                <br />
                                {request.addressLine2 && (
                                  <>
                                    {request.addressLine2}
                                    <br />
                                  </>
                                )}
                                {request.addressLine3 && (
                                  <>
                                    {request.addressLine3}
                                    <br />
                                  </>
                                )}
                                ({request.pinCode})
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={(e) => handleDeleteClick(e, request)}
                                className="text-red-500 hover:text-red-700"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable={false}
          theme="light"
        />

        {showBulkDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <h2 className="text-lg font-bold mb-4">Confirm Delete</h2>
              <p className="mb-6">
                Are you sure you want to delete {selectedRequests.size} selected {selectedRequests.size === 1 ? 'request' : 'requests'}?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBulkDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <h2 className="text-lg font-bold mb-4">Confirm Delete</h2>
              <p className="mb-6">
                Are you sure you want to delete the address for{" "}
                <span className="font-semibold">{deleteTarget.userName}</span>?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewDeleteRequest;
